<template>
  <!-- Pour tester cette page, lancer en debug mocha sur le serveur de notification, et récupérer l'url générée (services/pdfCreator.js)-->
  <b-container fluid class="">
    <!-- h1 tag mandatory : id "export-done" is used to notify puppeteer when the export is done. -->
    <h1 v-if="exportDone" id="export-done" class="m-3">
      {{ $t("global.exportDone") }}
    </h1>
    <h1 v-else class="m-3">
      {{ $t("global.exportInProgress") }}
    </h1>

    <div 
      v-for="data of maintenanceTablesData" 
      :id="exportDivId(data.tableNumber)"
      :key="maintenanceTableIdPrefix + data.tableNumber" 
      :data-cols-width="columnWidthList"
    >
      <table>
        <thead>
          <tr>
            <th :colspan="nbOfColumns" data-a-wrap="true" data-f-bold="true" data-f-sz="24">
              {{ $t("title.maintenance") }}
            </th>
          </tr>
        </thead>
        <!-- Ligne vide pour faire une ligne d'espace -->
        <tbody>
          <tr>
            <td />
          </tr>
        </tbody>
      </table>

      <table-renderer 
        class="export"
        custom-table-class="table-width-medium" 
        :table-id-prefix="maintenanceTableIdPrefix" 
        :table-number="data.tableNumber"
        :data-table="data.dataTable" 
        :dimension-fields="maintenanceDimensionColumnsTables"
        :measure-fields="maintenanceMeasureColumnsTables" 
        :table-header="data.tableHeader"
        :data-ready="maintenanceDataReadyTables" 
        :default-sorted-column="maintenanceDefaultSortedColumnTables" 
        :excel-attr-th="excelAttrTh"
      />
    </div>
  </b-container>
</template>

<script>
import { mapState } from "vuex";
import TableRenderer from "@/components/TableRenderer.vue";
import dimensionEnum from "@/model/dimensionEnum";
import { dateFormatEnum } from "@/filter/dateFormat";
import { ExportMailMaintenance } from "@/router/routeNames";
import TableTools from "@/utils/tableTools";
import { exportHelper } from "@/utils/exportHelper";
import moment from "moment";
import ExcelJS from "exceljs";
import TableToExcel from "@linways/table-to-excel";
import { excelStyles } from "@/scss/excelStyles";
import { getExportNumFormat } from "@/filter/exportFormat";

export default {
  name: ExportMailMaintenance,
  components: {
    TableRenderer,
  },
  data() {
    return {
      commonFilters: [],
      exportDone: false,
      nbOfColumns: 0,
    };
  },
  computed: {
    ...mapState({
      maintenanceDimensionColumnsTables: (state) =>
        state.maintenance.dimensionColumnsTables,
      maintenanceMeasureColumnsTables: (state) =>
        state.maintenance.measureColumnsTables,
      maintenanceGroupHeaders: (state) => state.maintenance.groupHeaders,
      maintenanceDefaultSortedColumnTables: (state) =>
        state.maintenance.defaultSortedColumnTables,
      maintenanceTablesData: (state) => state.maintenance.tablesData,
      maintenanceHideGroupsByTableNumber: (state) =>
        state.maintenance.hideGroupsByTableNumber,
      maintenanceDataReadyTables: (state) => state.maintenance.dataReadyTables,
    }),
    allDataReadyTables() {
      return this.maintenanceDataReadyTables;
    },
    maintenanceTableIdPrefix() {
      return "maintenance";
    },
    excelAttrTh() {
      return excelStyles.baseThAttr;
    },
    /** Largeur de chaque colonne : pour l'instant la même taille pour chaque colonne,
     * à voir pour customiser en fonction de leur contenu
     */
    columnWidthList() {
      const colWidth = 200 / this.nbOfColumns;
      return Array(this.nbOfColumns).fill(colWidth);
    },
  },
  watch: {},
  mounted: async function () {
    try {
      this.$store.dispatch("maintenance/resetDataReadyTables");
      this.$store.dispatch("maintenance/initDisplayTables");

      const queryFilters =
        this.$route.query.filter != null
          ? JSON.parse(decodeURIComponent(this.$route.query.filter))
          : null;

      if (queryFilters != null) {
        const siteFilter = {
          dimension: dimensionEnum.SITE,
          operator: "equals",
          values: queryFilters.siteIds,
        };
        const momentStartDate = moment(
          queryFilters.dateRange[0],
          dateFormatEnum.SQL_DATE_FORMAT
        );
        const momentEndDate =
          queryFilters.dateRange.length > 1
            ? moment(queryFilters.dateRange[1], dateFormatEnum.SQL_DATE_FORMAT)
            : momentStartDate;

        if (momentStartDate.isValid() && momentEndDate.isValid()) {
          const nextDay = moment(
            queryFilters.dateRange[0],
            dateFormatEnum.SQL_DATE_FORMAT
          ).add(1, "days");
          this.commonFilters = [
            siteFilter,
            {
              or: [
                {
                  and: [
                    {
                      dimension:
                        dimensionEnum.SMARTTOUCHMAINTENANCE_END_OF_MAINTENANCE,
                      operator: "equals",
                      values: [null],
                    },
                    {
                      dimension: dimensionEnum.SMARTTOUCHMAINTENANCE_EVENT_DATE,
                      operator: "beforeDate",
                      values: [nextDay],
                    },
                  ],
                },
                {
                  dimension:
                    dimensionEnum.SMARTTOUCHMAINTENANCE_END_OF_MAINTENANCE,
                  operator: "inDateRange",
                  values: queryFilters.dateRange,
                },
              ],
            },
            {
              dimension: dimensionEnum.SMARTTOUCH_ISDELETED,
              operator: "equals",
              values: ["0"],
            },
          ];
          const siteValues = await this.getSites([siteFilter]);

          await this.updateAllTableLists(siteValues);

          await this.createXlsx(momentStartDate, momentEndDate, siteValues);
        } else {
          console.error(
            `Invalid date range received in ExportMail page : ${queryFilters.dateRange}.`
          );
        }
      } else {
        console.error("No filter have been received in ExportMail page.");
      }
    } catch (e) {
      console.error(e);
      console.error("Unexpected exception occured : " + e.message);
    }
  },
  methods: {
    exportDivId(number) {
      return `maintenance-report-id-${number}`;
    },
    async getSites(siteFilter) {
      const query = {
        dimensions: [dimensionEnum.SITE, dimensionEnum.SITE_NAME],
        filters: siteFilter,
      };
      const resultSet = await TableTools.Helper.doRequest(query);
      return resultSet.map((o) => {
        return {
          id: o[dimensionEnum.SITE],
          label: o[dimensionEnum.SITE_NAME],
        };
      });
    },
    async updateAllTableLists(siteValues) {
      await this.$store.dispatch("maintenance/updateTableList", {
        filters: this.commonFilters,
        siteValues: siteValues,
        withCategoriesRows: false,
      });
      this.populate();

      this.nbOfColumns =
        this.maintenanceDimensionColumnsTables.length +
        this.maintenanceMeasureColumnsTables.length;
    },

    async populate() {
      const self = this;
      let dimensionColumnsData = [...self.maintenanceDimensionColumnsTables];
      let measureColumnsData = [...self.maintenanceMeasureColumnsTables];
      this.dimensionFields = dimensionColumnsData.map((col) => {
        col.tdAttr = (value, key, item) => {
          let tempAttr = excelStyles.getTdAttr(col, item);
          tempAttr["data-t"] = "n";
          tempAttr["data-num-fmt"] = getExportNumFormat(value, key);
          if (!tempAttr["data-num-fmt"]) tempAttr["data-t"] = "s";
          return tempAttr;
        };

        col.thAttr = (value, key, item) => {
          return excelStyles.getThAttr(col, item);
        };

        col.formatter = (value, key) => {
          if (value === 0) return null;
          if (!getExportNumFormat(value, key)) {
            return TableTools.Helper.valueFormatter(value, key);
          }
          return value;
        };
        return col;
      });

      measureColumnsData = measureColumnsData.map((col) => {
        col.tdAttr = (value, key, item) => {
          let tempAttr = excelStyles.getTdAttr(col, item);
          tempAttr["data-t"] = "n";
          tempAttr["data-num-fmt"] = getExportNumFormat(value, key);
          if (!tempAttr["data-num-fmt"]) tempAttr["data-t"] = "s";
          return tempAttr;
        };

        col.thAttr = (value, key, item) => {
          return excelStyles.getThAttr(col, item);
        };

        col.formatter = (value, key) => {
          if (value === 0) return null;
          if (!getExportNumFormat(value, key)) {
            return TableTools.Helper.valueFormatter(value, key);
          }
          return value;
        };
        return col;
      });
      await this.$store.commit(
        "maintenance/setDimensionColumnsTables",
        dimensionColumnsData
      );
      await this.$store.commit(
        "maintenance/setMeasureColumnsTables",
        measureColumnsData
      );
    },

    async createXlsx(momentStartDate, momentEndDate) {
      const self = this;
      if (self.allDataReadyTables) {
        //File Title
        const filename = `Rapports_${momentStartDate.format(
          dateFormatEnum.DATE_FORMAT_FOR_FILENAME
        )}_${momentEndDate.format(dateFormatEnum.DATE_FORMAT_FOR_FILENAME)}`;
        const title = self.$t("export.mailmaintenancetitle");

        let wb = new ExcelJS.Workbook();
        self.maintenanceTablesData.forEach((table) => {
          const sheet = document.getElementById(
            `maintenance-report-id-${table.tableNumber}`
          );
          wb = TableToExcel.tableToSheet(wb, sheet, {
            autoStyle: false,
            sheet: {
              name: exportHelper.getValidSheetName(table.tableHeader),
            },
          });
        });

        if (self.$route.query.outputmode == "inmemory") {
          const result = await wb.xlsx.writeBuffer({ filename: title });
          const blob = new Blob([result], {
            type:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const reader = new FileReader();
          reader.onloadend = () => {
            window.xlsxdata = reader.result;
            self.exportDone = true;
          };
          reader.readAsDataURL(blob);
        } else {
          TableToExcel.save(wb, `${filename}.xlsx`);
          setTimeout(function () {
            window.close();
          }, 500);
        }
      } else {
        console.error("Data is not ready for export");
      }
    },
  },
};
</script>
